import { cx } from 'class-variance-authority';

import type { SectionData } from '@/app/[locale]/how-it-works/_data/timelineSections';
import Motion, { type MotionProps } from '@/app/components/Motion';

import HeartRateAppUI from './HeartRateAppUI';
import SecondaryAppUI from './SecondaryAppUI';
import { shouldDelayMotionTransition } from './utils/shouldDelayMotionTransition';

export interface AppUIWrapperProps {
  readonly activeIndex: number;
  readonly previousIndex: number;
  readonly sections: SectionData[];
}

export const getMotionProps = ({
  activeIndex,
  delayMotion,
  sectionCount,
}: {
  activeIndex: number;
  delayMotion: boolean;
  sectionCount: number;
}): MotionProps => {
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === sectionCount - 1;
  const isFullscreen = isFirst || isLast;
  return {
    animate: {
      height: isFullscreen ? '100vh' : '50vw',
    },
    initial: {
      height: isFullscreen ? '100vh' : '50vw',
    },
    transition: {
      delay: delayMotion ? 0.5 : 0,
      duration: 0.5,
    },
  };
};

const AppUIWrapper = ({
  activeIndex,
  previousIndex,
  sections,
}: AppUIWrapperProps): JSX.Element => {
  const sectionCount = sections.length;
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === sectionCount - 1;
  const isFullscreen = isFirst || isLast;
  const delayMotion = shouldDelayMotionTransition({
    activeIndex,
    previousIndex,
    sectionCount,
  });
  const motionProps = getMotionProps({
    activeIndex,
    delayMotion,
    sectionCount,
  });
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 z-20 hidden size-full lg:js:block"
      data-cy="timeline_app_ui_wrapper"
    >
      <div className="sticky top-0 h-screen w-screen">
        <Motion
          {...motionProps}
          className={cx(
            'absolute top-1/2 flex w-full -translate-y-1/2 justify-end',
            {
              'motion-reduce:!h-screen': isFullscreen,
              'motion-reduce:!h-[50vw] lg:max-h-[80vh]': !isFullscreen,
            },
          )}
          data-cy="timeline_app_ui_wrapper_motion"
        >
          <div className="m-10 flex w-[17.361vw] max-w-[250px] flex-col justify-end gap-y-3">
            <div className="relative w-full">
              {sections.map(({ id, secondaryAppUi }, i) => (
                <SecondaryAppUI
                  {...secondaryAppUi}
                  activeIndex={activeIndex}
                  index={i}
                  key={`${id}_secondary_app_ui`}
                />
              ))}
            </div>
            <HeartRateAppUI
              activeIndex={activeIndex}
              previousIndex={previousIndex}
            />
          </div>
        </Motion>
      </div>
    </div>
  );
};

export default AppUIWrapper;
